import { useEffect, useState } from "react";
import { ApiClientError, fetchSummary, fetchGcr } from "../api/client";
import { StateBanner } from "../components/StateBanner";
import { PeriodSwitcher } from "../components/PeriodSwitcher";
import { BarChart } from "../components/BarChart";
import { GcrCard } from "../components/GcrCard";
import { Badge } from "../components/Badge";
import { channelMeta, CHANNEL_STATE_META, yen } from "../lib/meta";
import { usePeriod } from "../lib/usePeriod";
import type { GcrResponse, SummaryChannel, SummaryResponse } from "../types/contract";

type FetchState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; data: SummaryResponse };

/** shopify/rakuten（実売上を持つチャネル）かどうか。表示レイアウトの出し分けに使う。 */
function isSalesChannel(ch: SummaryChannel): boolean {
  return ch.channel === "shopify" || ch.channel === "rakuten";
}

export function DashboardPage() {
  const { period, customStart, customEnd, range, setPeriod, setCustomStart, setCustomEnd } = usePeriod();
  const [state, setState] = useState<FetchState>({ status: "loading" });
  // GCR 小カード（2026-10-19）。summary とは独立に取り、失敗しても summary の表示は止めない（worker 未デプロイでも落ちない）。
  const [gcr, setGcr] = useState<GcrResponse | null>(null);
  const [gcrTick, setGcrTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    fetchGcr(range.from, range.to)
      .then((d) => {
        if (!cancelled) setGcr(d);
      })
      .catch(() => {
        if (!cancelled) setGcr(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, gcrTick]);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    fetchSummary(range.from, range.to)
      .then((data) => {
        if (!cancelled) setState({ status: "ok", data });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const message = e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。";
        setState({ status: "error", message });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  const retry = () => {
    setState({ status: "loading" });
    fetchSummary(range.from, range.to)
      .then((data) => setState({ status: "ok", data }))
      .catch((e: unknown) => {
        const message = e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。";
        setState({ status: "error", message });
      });
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">ダッシュボード</h1>
        <PeriodSwitcher
          period={period}
          onChange={setPeriod}
          customStart={customStart}
          customEnd={customEnd}
          onCustomStartChange={setCustomStart}
          onCustomEndChange={setCustomEnd}
        />
      </div>

      {state.status === "loading" && <SkeletonCards />}
      {state.status === "error" && <StateBanner kind="error" message={state.message} onRetry={retry} />}
      {state.status === "ok" && (
        <>
          {state.data.sources.missing_dates.length > 0 && (
            <StateBanner
              kind="missing"
              message={`${state.data.sources.missing_dates.join(", ")} の日次データが未取得です（最新取得日: ${
                state.data.sources.daily_json_latest ?? "—"
              }）`}
            />
          )}
          <div className="card-grid">
            {state.data.channels.map((ch) => (
              <ChannelCard key={ch.channel} ch={ch} />
            ))}
          </div>

          {gcr && <GcrCard data={gcr} onChanged={() => setGcrTick((t) => t + 1)} />}

          <div className="card">
            <h2 className="section-title">週次支出・売上推移</h2>
            <BarChart
              data={state.data.weekly.map((w) => ({ label: w.week, value: w.spend }))}
              series2={state.data.weekly.map((w) => ({ label: w.week, value: w.revenue }))}
              color="var(--accent)"
              color2="var(--ch-shopify-c)"
              legend={[
                { label: "広告費（Meta＋Google＋Amazon）", color: "var(--accent)" },
                { label: "売上（Shopify+楽天）", color: "var(--ch-shopify-c)" },
              ]}
            />
          </div>
        </>
      )}
    </div>
  );
}

/**
 * 広告費の媒体内訳（2026-10-17・Google 広告の合流、2026-10-23・Amazon 広告の合流）。worker が spend_breakdown を返し、
 * かつ Meta 以外の媒体が1つでも取れている（null でない）ときだけ出す。旧 worker（フィールド無し）では何も出さない。
 */
function SpendBreakdown({ ch }: { ch: SummaryChannel }) {
  const b = ch.spend_breakdown;
  if (!b) return null;
  const parts: string[] = [];
  if (b.meta !== null && b.meta !== undefined) parts.push(`Meta ${yen(b.meta)}`);
  if (b.rpp !== null && b.rpp !== undefined) parts.push(`RPP ${yen(b.rpp)}`);
  if (b.google !== null && b.google !== undefined) parts.push(`Google ${yen(b.google)}`);
  // Amazon は広告費 >0 のときだけ内訳に出す（Amazon 以外のカードに ¥0 を並べない・契約 §7）
  const hasAmazon = (b.amazon ?? 0) > 0;
  if (hasAmazon) parts.push(`Amazon ${yen(b.amazon as number)}`);
  const hasNonMeta = (b.rpp !== null && b.rpp !== undefined) || (b.google !== null && b.google !== undefined) || hasAmazon;
  if (!hasNonMeta || parts.length < 2) return null;
  return <div className="ad-note">内訳: {parts.join(" ・ ")}</div>;
}

function ChannelCard({ ch }: { ch: SummaryChannel }) {
  const meta = channelMeta(ch.channel);
  const stateBadge = ch.state !== "ok" ? CHANNEL_STATE_META[ch.state] : null;

  return (
    <div className="channel-card">
      <div className="ch-label">
        <span className="dot" style={{ background: meta.c }} />
        {ch.label}
      </div>

      {ch.state === "no_data" ? (
        <div className="state-badge-row">
          <Badge label={stateBadge!.label} color={stateBadge!.c} bg={stateBadge!.bg} />
        </div>
      ) : isSalesChannel(ch) ? (
        <>
          <div className="spend">{ch.revenue !== null ? yen(ch.revenue) : "—"}</div>
          <div className="metric-row">
            <span>注文数</span>
            <b>{ch.orders !== null ? `${ch.orders.toLocaleString("ja-JP")}件` : "—"}</b>
          </div>
          {ch.spend !== null && ch.spend > 0 ? (
            <>
              <div className="metric-row">
                <span>広告費</span>
                <b>{yen(ch.spend)}</b>
              </div>
              <div className="metric-row">
                <span>ROAS</span>
                <b>{ch.roas !== null ? `${ch.roas.toFixed(2)}x` : "—"}</b>
              </div>
              <SpendBreakdown ch={ch} />
            </>
          ) : (
            <div className="metric-row faint">
              <span>広告費</span>
              <b>{ch.spend === null ? "未連携" : "¥0"}</b>
            </div>
          )}
          {ch.ad_note && <div className="ad-note">※{ch.ad_note}</div>}
          {stateBadge && (
            <div className="state-badge-row">
              <Badge label={stateBadge.label} color={stateBadge.c} bg={stateBadge.bg} />
            </div>
          )}
        </>
      ) : (
        <>
          <div className="spend">{ch.spend !== null ? yen(ch.spend) : "—"}</div>
          <div className="metric-row">
            <span>{ch.primary.label}</span>
            <b>{ch.primary.value ?? "—"}</b>
          </div>
          <div className="metric-row">
            <span>{ch.sub.label}</span>
            <b>{ch.sub.value ?? "—"}</b>
          </div>
          {/* Amazon 広告は売上を連携していない（Amazon 計測値は別基準）ため、売上は「未連携」と明示する */}
          {ch.channel === "amazon" && ch.revenue === null && (
            <div className="metric-row faint">
              <span>売上</span>
              <b>未連携</b>
            </div>
          )}
          {ch.spend_breakdown &&
            ((ch.spend_breakdown.google ?? 0) > 0 || (ch.spend_breakdown.amazon ?? 0) > 0) && <SpendBreakdown ch={ch} />}
          {stateBadge && (
            <div className="state-badge-row">
              <Badge label={stateBadge.label} color={stateBadge.c} bg={stateBadge.bg} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SkeletonCards() {
  return (
    <div className="card-grid">
      {[0, 1, 2, 3].map((i) => (
        <div className="channel-card" key={i}>
          <div className="skeleton" style={{ height: 14, width: "60%", marginBottom: 12 }} />
          <div className="skeleton" style={{ height: 24, width: "80%", marginBottom: 10 }} />
          <div className="skeleton" style={{ height: 12, width: "50%" }} />
        </div>
      ))}
    </div>
  );
}
